import { useMemo } from 'react';
import * as R from 'ramda';

import UtcDateCell from 'App/components/Table/Cells/UtcDateCell';
import { useUserContext } from 'App/contexts/UserContext';
import {
    ActivityDueDateCell,
    ActivityNameCell,
    ActivityStatusCell,
    LastUpdatedCell,
    PatientIdCell,
    PatientNameCell,
    MedicationAssociationInsuranceCell,
} from '../components/Cell';
import propOrDash from 'App/utils/propOrDash';
import localize from 'App/utils/localize';

const displayIdMap = {
    external_id: 'externalId',
    other_id: 'otherId',
    ehr_id: 'ehrId',
};

export const useGetHeaderMapping = ({ isAssignmentManager = false } = {}) => {
    const user = useUserContext();
    const displayedId = R.path(['company_permissions', 'DisplayedId'], user);

    const headerMapping = useMemo(
        () => ({
            patient_id: {
                sortName: Object.keys(displayIdMap).includes(displayedId) ? displayedId : 'human_id',
                accessor: propOrDash(displayIdMap[displayedId] || 'humanId'),
                canSort: true,
                filterName: displayIdMap[displayedId] || 'humanId',
                Cell: isAssignmentManager ? undefined : PatientIdCell,
            },
            patient_name: {
                canSort: true,
                Cell: isAssignmentManager ? undefined : PatientNameCell,
                filterName: 'patientName',
                sortName: 'patient_name',
            },
            medication: {
                canSort: false,
                filterName: 'medication',
            },
            activity_due_date: {
                accessor: 'dateDueSearch',
                sortName: 'date_due',
                canSort: true,
                Cell: isAssignmentManager ? UtcDateCell : ActivityDueDateCell,
                filterName: 'dateDueSearch',
            },
            activity_name: {
                accessor: propOrDash('activityName'),
                canSort: true,
                Cell: isAssignmentManager ? undefined : ActivityNameCell,
                filterName: 'activityName',
            },
            patient_dob: {
                accessor: propOrDash('patientDob'),
                canSort: true,
                Cell: UtcDateCell,
                filterName: 'patientDob',
            },
            activity_status: {
                accessor: propOrDash('activityStatus'),
                canSort: true,
                Cell: isAssignmentManager ? undefined : ActivityStatusCell,
                filterName: 'activityStatus',
            },
            last_updated: {
                accessor: propOrDash('updatedOnDisplay'),
                canSort: true,
                sortName: 'updated_on',
                filterName: 'updatedName',
                Cell: LastUpdatedCell,
            },
            patient_labels: {
                accessor: propOrDash('patientLabels'),
                canSort: true,
                sortName: 'patient_labels',
                filterName: 'patientLabels',
            },
            activity_needs_by_date: {
                accessor: propOrDash('activity_needs_by_date'),
                canSort: true,
                sortName: 'needs_by',
                filterName: 'needsBySearch',
            },
            assigned_to: {
                /**
                 * The assignedTo is the ID, but the assignedToName is the display value
                 */
                accessor: propOrDash('assignedToName'),
                canSort: true,
                filterName: 'assignedTo',
            },
            branch_name: {
                accessor: propOrDash('branchName'),
                canSort: true,
                filterName: 'branchName',
            },
            patient_state: {
                accessor: propOrDash('patientState'),
                canSort: true,
                filterName: 'patientState',
            },
            patient_status: {
                accessor: propOrDash('patientStatus'),
                canSort: true,
                filterName: 'patientStatus',
            },
            insurance_plan_name: {
                accessor: propOrDash('insurancePlanName'),
                canSort: true,
                Cell: MedicationAssociationInsuranceCell,
                filterName: 'insurancePlanName',
            },
            activity_status_date: {
                accessor: R.compose(localize, propOrDash('activityStatusDate')),
                canSort: true,
                filterName: 'activityStatusDate',
            },
            medication_association: {
                accessor: propOrDash('medicationAssociation'),
                canSort: true,
                Cell: MedicationAssociationInsuranceCell,
                filterName: 'medicationAssociation',
            },
        }),
        [displayedId, isAssignmentManager]
    );

    return headerMapping;
};
